/** Plan construction, ids, defaults, and the clone-then-mutate wrapper. */
import { POLICY } from './policy';
import { clone } from './geom';
import { countableId } from './sanitize';
import type { Plan, PlanDefaults } from './types';

/** Plan defaults as the policy file sets them (F13 starting point). */
export function policyDefaults(): PlanDefaults {
  const w = POLICY.window_default;
  return {
    ceilingFt: POLICY.ceiling_default_ft,
    interiorWallIn: POLICY.interior_wall_in,
    exteriorWallIn: POLICY.exterior_wall_default_in,
    opening: { kind: POLICY.opening_default.kind, widthFt: POLICY.opening_default.width_ft },
    doorInterior: { type: POLICY.door_default_interior.type, widthFt: POLICY.door_default_interior.width_ft },
    doorExterior: { type: POLICY.door_default_exterior.type, widthFt: POLICY.door_default_exterior.width_ft },
    window: {
      widthFt: w.width_ft, heightFt: w.height_ft, style: w.style, material: w.material, grid: w.grid,
      glazing: w.glazing, impact: w.impact, grade: w.grade, trim: w.trim,
    },
    doorAsked: false,
    windowAsked: false,
  };
}

export function blankPlan(defaults?: PlanDefaults): Plan {
  return { rooms: [], defaults: defaults ? clone(defaults) : policyDefaults(), next: 1 };
}

/** Next numeric id (corners, openings, links). */
export const uid = (plan: Plan): number => plan.next++;

/** Room ids: the screen passes a UUID; otherwise a plan-unique "r<n>". */
export const roomId = (plan: Plan, id?: string): string => id ?? `r${uid(plan)}`;

/**
 * Recompute `next` above every numeric id in the plan (after loading rooms). Only whole
 * numbers count (`countableId`): a stored NaN, 1e300 or string id never moves the counter.
 */
export function reindexNext(plan: Plan): void {
  let max = 0;
  const see = (v: unknown) => { if (countableId(v)) max = Math.max(max, v); };
  for (const r of plan.rooms) {
    r.corners.forEach((v) => see(v.id));
    r.openings.forEach((o) => {
      see(o.id);
      see(o.link);
      // W2: the openings a removed wall keeps for Restore wall come back with their ids.
      const kept: unknown = o.missing?.removedOpenings;
      if (Array.isArray(kept)) kept.forEach((x: { opening?: { id?: unknown; link?: unknown } } | null) => { see(x?.opening?.id); see(x?.opening?.link); });
    });
  }
  if (!countableId(plan.next)) plan.next = 1;
  plan.next = Math.max(plan.next, max + 1);
}

/**
 * Convention helper: every core action mutates the plan it is given. A UI that
 * wants a new object per change calls `withPlan(plan, (p) => action(p, ...))`
 * and gets a deep-cloned plan back with the action's result.
 */
export function withPlan<T>(plan: Plan, fn: (p: Plan) => T): { plan: Plan; result: T } {
  const p = clone(plan);
  const result = fn(p);
  return { plan: p, result };
}
